import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { createSelector } from 'reselect'
import { View } from 'components/View'
import Button from 'components/Button'
import { shouldShowConsentBannerSelector } from '../../selectors/should-show-banner'
import { grantAllConsentAction } from '../../actions/grant-all'
import { denyAllConsentAction } from '../../actions/deny-all'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import { useTheme } from 'modules/styles'
import { logUtil } from 'packages/src'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import isSmallScreenViewportSelect from 'modules/viewport/selectors/is-small-screen'

const messages = defineMessages({
  title: {
    id: 'ConsentBanner.title',
    defaultMessage: 'We value your privacy'
  },
  description: {
    id: 'ConsentBanner.description',
    defaultMessage: 'We use cookies and similar technologies to enhance your experience, analyze site traffic, and deliver personalized content. By clicking "Accept All", you consent to our use of cookies for analytics and marketing. You can manage your preferences anytime in our privacy settings.'
  },
  descriptionShort: {
    id: 'ConsentBanner.descriptionShort',
    defaultMessage: 'We use cookies and similar technologies to enhance your experience, analyze site traffic, and deliver personalized content.'
  },
  acceptAll: {
    id: 'ConsentBanner.acceptAll',
    defaultMessage: 'Accept All'
  },
  rejectAll: {
    id: 'ConsentBanner.rejectAll',
    defaultMessage: 'Reject All'
  },
  privacyPolicy: {
    id: 'ConsentBanner.privacyPolicy',
    defaultMessage: 'Privacy Policy'
  },
  readMore: {
    id: 'ConsentBanner.readMore',
    defaultMessage: 'Read more'
  },
  readLess: {
    id: 'ConsentBanner.readLess',
    defaultMessage: 'Read less'
  }
})

export const ConsentBanner = ({ shouldShow, dispatch }) => {
  const intl = useIntl()
  const theme = useTheme()
  const logger = logUtil('consent:ConsentBanner')
  const [isClient, setIsClient] = React.useState(false)
  const [isExpanded, setIsExpanded] = React.useState(false)
  const isSmallScreen = useSelector(isSmallScreenViewportSelect)

  // Only render on client-side (not SSR) since we need localStorage
  React.useEffect(() => {
    setIsClient(true)
  }, [])

  React.useEffect(() => {
    if (shouldShow && isClient) {
      logger.info('Consent banner displayed to user')
    }
  }, [shouldShow, isClient])

  // Don't render during SSR
  if (!isClient) {
    return null
  }

  if (!shouldShow) {
    logger.debug('Banner hidden (shouldShow=false)')
    return null
  }

  const handleAcceptAll = () => {
    logger.info('User clicked "Accept All"')
    dispatch(grantAllConsentAction())
  }

  const handleRejectAll = () => {
    logger.info('User clicked "Reject All"')
    dispatch(denyAllConsentAction())
  }

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded)
    logger.debug(`User toggled description: ${!isExpanded ? 'expanded' : 'collapsed'}`)
  }

  return (
    <View
      style={{
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        background: '#ffffff',
        zIndex: '1001',
        boxShadow: '0 -0.11em 0.88em rgba(0, 0, 0, 0.15)',
        padding: '1.33em',
        borderTop: '0.055em solid #e6e6e6'
      }}
    >
      <View
        style={{
          maxWidth: '75em',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1em'
        }}
      >
        <View
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.55em'
          }}
        >
          <h3
            style={{
              fontSize: '1.11em',
              fontWeight: '600',
              margin: '0',
              color: '#333333'
            }}
          >
            {formatMessageIntlUtil(intl, messages.title)}
          </h3>
          <View>
            <p
              style={{
                fontSize: '0.88em',
                lineHeight: '1.5',
                margin: '0',
                color: '#666666'
              }}
            >
              {isSmallScreen && !isExpanded
                ? formatMessageIntlUtil(intl, messages.descriptionShort)
                : formatMessageIntlUtil(intl, messages.description)}
            </p>
            {isSmallScreen && (
              <button
                onClick={handleToggleExpanded}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0099e5',
                  cursor: 'pointer',
                  fontSize: '0.77em',
                  padding: '0.33em 0 0 0',
                  textDecoration: 'underline',
                  marginTop: '0.33em',
                  textAlign: 'left'
                }}
              >
                {isExpanded
                  ? formatMessageIntlUtil(intl, messages.readLess)
                  : formatMessageIntlUtil(intl, messages.readMore)}
              </button>
            )}
          </View>
        </View>

        <View
          row
          style={{
            gap: '0.88em',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}
        >
          <Button
            onClick={handleAcceptAll}
            name='accept-all-cookies'
            aria-label='Accept all cookies'
            style={{
              backgroundColor: theme.colors.primary || '#0099e5',
              color: '#ffffff',
              padding: '0.66em 1.55em',
              borderRadius: '0.27em',
              fontWeight: '500',
              fontSize: '0.88em'
            }}
          >
            {formatMessageIntlUtil(intl, messages.acceptAll)}
          </Button>

          <Button
            onClick={handleRejectAll}
            name='reject-all-cookies'
            aria-label='Reject all cookies'
            style={{
              backgroundColor: '#f5f5f5',
              color: '#333333',
              padding: '0.66em 1.55em',
              borderRadius: '0.27em',
              fontWeight: '500',
              fontSize: '0.88em',
              border: '0.055em solid #cccccc'
            }}
          >
            {formatMessageIntlUtil(intl, messages.rejectAll)}
          </Button>

          <a
            href='/privacy'
            style={{
              fontSize: '0.77em',
              color: '#0099e5',
              textDecoration: 'underline',
              marginLeft: '0.55em'
            }}
          >
            {formatMessageIntlUtil(intl, messages.privacyPolicy)}
          </a>
        </View>
      </View>
    </View>
  )
}

ConsentBanner.propTypes = {
  shouldShow: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired
}

const select = () => createSelector(
  shouldShowConsentBannerSelector(),
  (shouldShow) => ({
    shouldShow
  })
)

export const ConnectedConsentBanner = connect(select)(ConsentBanner)
